import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { create } from '../../actions/search-subscription';
import {
  trackLoginModuleClickLoginSaveSearchResult,
  trackLoginModuleInViewportSaveSearchResult,
} from '../../actions/tracking';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import Alert from '../../styled-components/components/Alert';
import { Button } from '../../styled-components/components/Button';
import Content from '../../styled-components/components/Content';
import InputHelp from '../../styled-components/components/InputHelp';
import InputLabel from '../../styled-components/components/InputLabel';
import InputText from '../../styled-components/components/InputText';
import Loader from '../../styled-components/components/Loader';
import Modal from '../../styled-components/components/Modal';
import { StackMedium } from '../../styled-components/components/Stack';
import SignInModal from '../../styled-components/structures/Modals/SignInModal';

interface SearchSubscriptionProps {
  query: string;
  onClose: () => void;
}

const SearchSubscription = ({ query, onClose }: SearchSubscriptionProps) => {
  const { t } = useTranslation(['common', 'list']);
  const dispatch = useDispatch();

  const authenticated = useSelector<RootState, boolean>((s) => !!s.auth.token);

  const requestStatus = useSelector<RootState, number>((s) => s.subscriptions.search.requestStatus);
  const responseStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.search.responseStatus,
  );

  const requestCreate = useDataExchangeStatus(ExchangeTypes.Creating, [
    requestStatus,
    responseStatus,
  ]);

  const actions = bindActionCreators(
    {
      create,
      trackLoginModuleInViewportSaveSearchResult,
      trackLoginModuleClickLoginSaveSearchResult,
    },
    dispatch,
  );

  const [name, setName] = useState('');

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value);

  if (requestCreate.pending) {
    return (
      <Modal heading={t('createMonitoring', { ns: 'list' })} onClose={onClose}>
        <Loader />
      </Modal>
    );
  }

  if (!authenticated) {
    const url = new URL(location.href);
    url.searchParams.append('subscribe', 'true');
    const authUrl = `/auth/signin?redirectUrl=${encodeURIComponent(url.pathname + url.search)}`;
    return (
      <SignInModal
        image="/images/modal/search_subscription.png"
        title={t('createMonitoringModalTitle', { ns: 'list' })}
        text={t('loginModalDescription', { ns: 'loginModal' })}
        to={authUrl}
        loginButtonTestId="save-search-login-button"
        onClose={onClose}
        trackOnInit={actions.trackLoginModuleInViewportSaveSearchResult}
        trackOnSignInClick={actions.trackLoginModuleClickLoginSaveSearchResult}
      />
    );
  }

  if (requestCreate.successful) {
    return (
      <Modal
        heading={t('monitoringHasBeenCreated', { ns: 'list' })}
        onClose={onClose}
        footerActions={[
          <Button
            key="monitoring-close"
            onClick={onClose}
            title={t('closeModal', { ns: 'common' })}
            $tone="clear"
            $fullWidth
          >
            {t('close', { ns: 'common' })}
          </Button>,
        ]}
      >
        <Content>
          <p>{t('monitoringHasBeenCreatedDescription', { ns: 'list' })}</p>
        </Content>
      </Modal>
    );
  }

  let alert = null;
  if (requestCreate.failed) {
    alert = (
      <StackMedium>
        <Alert tone="error">{t('monitoringCouldNotBeCreated', { ns: 'list' })}</Alert>
      </StackMedium>
    );
  }

  return (
    <Modal
      heading={t('createMonitoring', { ns: 'list' })}
      onClose={onClose}
      footerActions={[
        <Button
          key="monitoring-cancel"
          onClick={onClose}
          title={t('cancel', { ns: 'common' })}
          $tone="clear"
          $fullWidth
          data-testid="save-search-modal-cancel-button"
        >
          {t('cancel', { ns: 'common' })}
        </Button>,
        <Button
          key="monitoring-save"
          onClick={() =>
            actions.create({
              query,
              name,
            })
          }
          title={t('save', { ns: 'common' })}
          $tone="primary"
          $fullWidth
          disabled={!name}
          data-testid="save-search-modal-save-button"
        >
          {t('save', { ns: 'common' })}
        </Button>,
      ]}
    >
      <StackMedium>
        <Alert tone="info">{t('monitoringHelp', { ns: 'list' })}</Alert>
      </StackMedium>
      <StackMedium>
        <InputLabel>{t('nameOfMonitoring', { ns: 'list' })}</InputLabel>
        <InputText
          placeholder={t('monitoringNamePlaceholder', { ns: 'list' })}
          id="create-subscription-name"
          data-testid="save-search-modal-name-input"
          value={name}
          onChange={onChange}
        />
        <InputHelp>{t('monitoringNameHelp', { ns: 'list' })}</InputHelp>
      </StackMedium>
      {alert}
    </Modal>
  );
};

export default SearchSubscription;
